import {
  ArrowRight,
  Building2,
  Clock,
  Code2,
  FlaskConical,
  GraduationCap,
  Home,
  LineChart,
  School,
  Users,
  Video,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';

import { FadeIn } from '@/components/custom/fade-in';
import { FaqAccordion } from '@/components/custom/faq-accordion';
import { Marquee } from '@/components/custom/marquee';
import { PopIn } from '@/components/custom/pop-in';
import { ScrollParallax } from '@/components/custom/scroll-parallax';
import { ACADEMY_URL, GET_STARTED_URL } from '@/components/custom/site-nav';
import { TiltReveal } from '@/components/custom/tilt-reveal';
import { TracingBeam } from '@/components/custom/tracing-beam';
import { AssetImage } from '@/components/custom/asset-image';
import { journeyFor } from '~/content/intake-journey';
import { clinicJsonLd, pageMeta } from '~/lib/meta';
import { joinNames, liveStateNames, liveStatesPhrase, statePath } from '~/lib/states';
import { getLocale } from '~/middleware/i18next';
import { Languages } from '~/localization/i18n';
import { getClinics, getStates } from '~/services/content.server';

export const meta = pageMeta<typeof loader>(({ loaderData, siteUrl, locale }) => {
  const states = loaderData?.states ?? [];
  // While there are few live states the title names them; past that, it goes general.
  const phrase = liveStatesPhrase(states, locale);
  return {
    title:
      locale === Languages.ES
        ? `BH Field — Terapia ABA${phrase ? ` en ${phrase}` : ''}: en casa, en la escuela y en la clínica`
        : `BH Field — ABA Therapy${phrase ? ` in ${phrase}` : ''}: home, school & clinic`,
    description:
      locale === Languages.ES
        ? `Terapia ABA para niños con autismo y discapacidades del desarrollo, en persona en casa y en la escuela${
            phrase ? ` en ${phrase}` : ''
          }, y en nuestra clínica de Peachtree City. Evaluaciones y capacitación para padres en persona o por telesalud.`
        : `ABA therapy for children with autism and developmental disabilities — in person at home and at school${
            phrase ? ` in ${phrase}` : ''
          }, and at our Peachtree City clinic. Assessments and parent training in person or by telehealth.`,
    path: '/',
    // A MedicalBusiness node per open clinic and office, built straight from the
    // `clinics` rows — the home page is the surface that describes every location.
    jsonLd: (loaderData?.clinics ?? [])
      .filter((clinic) => clinic.is_open)
      .map((clinic) => {
        const state = states.find((candidate) => candidate.code === clinic.state);
        return clinicJsonLd(clinic, siteUrl, locale, {
          pagePath: state ? statePath(state.slug) : '/',
          stateName: state?.name_en,
        });
      }),
  };
});

export async function loader({ context }: LoaderFunctionArgs) {
  // Stages come from the shared journey so this page cannot drift from /services
  // and the enrollment hub, which is exactly what happened before.
  const [clinics, states] = await Promise.all([getClinics(), getStates()]);
  return {
    clinics,
    states: states.map(({ code, slug, name_en, name_es, status }) => ({ code, slug, name_en, name_es, status })),
    stages: journeyFor(getLocale(context)),
  };
}

export default function HomePage() {
  const { states } = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation('home');
  // The live states, named — the coverage line used to be "Atlanta area & surrounding".
  const coverage = joinNames(liveStateNames(states, i18n.language), i18n.language);
  return (
    <>
      <Hero />
      <section aria-label={t('trust.newClients')} className="border-y border-border bg-background-alt py-5">
        <Marquee duration={30}>
          {[
            t('trust.newClients'),
            t('trust.language'),
            t('trust.coverage', { states: coverage }),
            t('trust.settings'),
            t('trust.technology'),
          ].map((item) => (
            <span key={item} className="flex items-center">
              <span className="font-mono text-[11px] tracking-[0.88px] whitespace-nowrap text-muted-foreground uppercase">
                {item}
              </span>
              <span aria-hidden="true" className="mx-8 size-[3px] rounded-full bg-muted-foreground/60" />
            </span>
          ))}
        </Marquee>
      </section>
      <WhyAba />
      <Services />
      <Clinic />
      <Process />
      <TechResearch />
      <Academy />
      <Testimonial />
      <Faq />
      <FinalCta />
    </>
  );
}

// Home-only artwork: each scene illustrates a practical moment of learning or support.
// Versioned keys keep shared artwork and existing deployments intact.
const HOME_ART = '/images/home/everyday-moments-v1';

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  const { t } = useTranslation('home');
  return (
    <section className="bg-background px-4 md:px-6 lg:px-8 xl:px-24 pt-8 md:pt-10 lg:pt-12 pb-12 md:pb-16 lg:pb-24">
      <div className="mx-auto flex max-w-[1248px] flex-col gap-12 lg:flex-row lg:items-start lg:gap-16">
        <div className="flex flex-col gap-7 lg:flex-1">
          <FadeIn y={12} duration={0.6}>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.08] tracking-[-1.28px]">
              <span className="block font-bold italic text-primary">{t('hero.titleAccent')}</span>
              <span className="block font-bold text-foreground">{t('hero.titleRest')}</span>
            </h1>
          </FadeIn>
          <FadeIn y={8} duration={0.5} delay={0.15}>
            <p className="font-display max-w-[600px] text-xl md:text-2xl lg:text-[26px] italic leading-9 text-foreground-soft">
              {t('hero.subtitle')}
            </p>
          </FadeIn>
          <FadeIn y={6} duration={0.4} delay={0.3}>
            <p className="max-w-[560px] text-[17px] leading-7 text-foreground-soft">{t('hero.body')}</p>
          </FadeIn>
          <FadeIn y={4} duration={0.4} delay={0.45}>
            <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center">
              <a
                href={GET_STARTED_URL}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {t('hero.ctaPrimary')} <ArrowRight className="size-4" strokeWidth={2} />
              </a>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-6 py-3.5 text-[15px] font-medium text-foreground transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {t('hero.ctaSecondary')} <ArrowRight className="size-4" strokeWidth={2} />
              </Link>
            </div>
          </FadeIn>
        </div>

        <div className="flex w-full flex-col lg:w-[560px] lg:shrink-0">
          {/* Child-led play makes the hero's engagement message tangible.
              Transparent paper layers let the page background show through. */}
          <TiltReveal maxTilt={14}>
            <div className="relative aspect-[4/3] w-full">
              <AssetImage
                src={`${HOME_ART}/joyful-learning.webp`}
                alt=""
                width={800}
                height={600}
                className="absolute inset-0 h-full w-full object-contain"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </TiltReveal>
          <FadeIn y={6} duration={0.4} delay={0.55} whileInView>
            <div className="flex w-full flex-col gap-6 px-1 pt-8">
              <p className="font-mono text-xs tracking-[1.44px] text-primary uppercase">{t('hero.eyebrow')}</p>
              <div className="h-0.5 w-20 bg-primary" />
              <p className="font-display max-w-[640px] text-2xl md:text-[28px] italic leading-[1.36] text-foreground text-balance">
                {t('hero.pullQuote')}
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

// ─── Why ABA ──────────────────────────────────────────────────────────────────

const PILLARS = [
  { spot: `${HOME_ART}/expressing-needs.webp`, key: 'skillAcquisition' },
  { spot: `${HOME_ART}/shared-play.webp`, key: 'playBased' },
  { spot: `${HOME_ART}/everyday-independence.webp`, key: 'continuousImprovement' },
] as const;

function WhyAba() {
  const { t } = useTranslation('home');
  return (
    <section
      id="why-aba"
      aria-labelledby="why-aba-heading"
      className="scroll-mt-[120px] bg-background px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24"
    >
      <div className="mx-auto flex max-w-[1248px] flex-col gap-12 md:gap-16">
        <FadeIn whileInView y={12} duration={0.6}>
          <header className="flex flex-col gap-4">
            <p className="font-mono text-xs tracking-[1.44px] text-primary uppercase">{t('whyAba.eyebrow')}</p>
            <h2
              id="why-aba-heading"
              className="font-display text-3xl md:text-4xl lg:text-[56px] lg:leading-[64px] font-bold text-foreground"
            >
              {t('whyAba.titleStart')} <span className="italic text-primary">{t('whyAba.titleAccent')}</span>
            </h2>
            <p className="max-w-[560px] text-lg leading-7 text-foreground-soft">{t('whyAba.subtitle')}</p>
          </header>
        </FadeIn>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-12">
          {PILLARS.map(({ spot, key }, i) => (
            <FadeIn key={key} whileInView delay={i * 0.1}>
              <article className="flex flex-col gap-6">
                <AssetImage
                  src={spot}
                  alt=""
                  width={480}
                  height={360}
                  className="h-44 w-full max-w-[280px] self-start object-contain object-left"
                  loading="lazy"
                />
                <h3 className="font-display text-2xl lg:text-[26px] leading-8 font-bold text-foreground">
                  {t(`whyAba.pillars.${key}.title`)}
                </h3>
                <p className="text-[15px] leading-6 text-foreground-soft">{t(`whyAba.pillars.${key}.body`)}</p>
              </article>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Services ─────────────────────────────────────────────────────────────────

const SERVICES = [
  { icon: Building2, key: 'clinicBased' },
  { icon: Home, key: 'homeBased' },
  { icon: Video, key: 'telehealth' },
  { icon: School, key: 'schoolBased' },
  { icon: Users, key: 'parentTraining' },
  { icon: GraduationCap, key: 'bcbaConsultation' },
] as const;

function Services() {
  const { t } = useTranslation('home');
  return (
    <section
      id="services"
      aria-labelledby="services-heading"
      className="scroll-mt-[120px] bg-background-alt px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24"
    >
      <div className="mx-auto flex max-w-[1248px] flex-col gap-12 md:gap-14">
        <FadeIn whileInView y={12} duration={0.6}>
          <header className="flex flex-col gap-4">
            <p className="font-mono text-xs tracking-[1.44px] text-primary uppercase">{t('services.eyebrow')}</p>
            <h2
              id="services-heading"
              className="font-display text-3xl md:text-4xl lg:text-[56px] lg:leading-[64px] font-bold text-foreground"
            >
              {t('services.titleStart')} <span className="italic text-primary">{t('services.titleAccent')}</span>
            </h2>
            <p className="max-w-[600px] text-base leading-[26px] text-foreground-soft">{t('services.subtitle')}</p>
          </header>
        </FadeIn>
        <FadeIn whileInView delay={0.1}>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 self-start rounded-sm text-[15px] font-medium text-foreground transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t('services.viewAll')} <ArrowRight className="size-4" strokeWidth={2} />
          </Link>
        </FadeIn>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map(({ icon: Icon, key }, i) => (
            <PopIn key={key} delay={i * 0.08} className="h-full">
              <article className="flex h-full flex-col gap-5 rounded-2xl border border-border bg-card px-7 py-8 transition-all hover:-translate-y-0.5 hover:shadow-card-soft">
                <div className="flex size-14 items-center justify-center rounded-2xl bg-surface-pale">
                  <Icon className="size-6 text-foreground-soft" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-[22px] leading-7 font-bold text-foreground">
                  {t(`services.items.${key}.title`)}
                </h3>
                <p className="text-sm leading-[22px] text-foreground-soft">{t(`services.items.${key}.body`)}</p>
              </article>
            </PopIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Process ──────────────────────────────────────────────────────────────────

function Process() {
  const { stages } = useLoaderData<typeof loader>();
  const { t } = useTranslation('home');
  return (
    <section
      id="process"
      aria-labelledby="process-heading"
      className="scroll-mt-[120px] bg-background px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24"
    >
      <TracingBeam className="mx-auto max-w-[1248px]">
        <div className="flex flex-col gap-12 md:gap-16">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <FadeIn whileInView y={12} duration={0.6}>
              <header className="flex flex-col gap-4">
                <p className="font-mono text-xs tracking-[1.44px] text-primary uppercase">{t('process.eyebrow')}</p>
                <h2
                  id="process-heading"
                  className="font-display text-3xl md:text-4xl lg:text-[56px] lg:leading-[64px] font-bold text-foreground"
                >
                  {t('process.titleStart')} <span className="italic text-primary">{t('process.titleAccent')}</span>
                </h2>
                <p className="max-w-[600px] text-lg leading-7 text-foreground-soft">{t('process.subtitle')}</p>
              </header>
            </FadeIn>
            <FadeIn whileInView delay={0.15} className="hidden md:block md:shrink-0">
              <ScrollParallax distance={26}>
                <AssetImage
                  src={`${HOME_ART}/guided-intake.webp`}
                  alt=""
                  width={480}
                  height={360}
                  className="h-44 w-auto lg:h-52"
                  loading="lazy"
                />
              </ScrollParallax>
            </FadeIn>
          </div>
          {/*
            The same nine stages as /services and the enrollment hub, from
            `content/intake-journey`. The home page used to carry its own
            six-step copy in home.json, which folded the two insurance waits
            into one and omitted therapist matching — the version a parent then
            found contradicted on the other two pages.
          */}
          <ol className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {stages.map((stage, i) => (
              <li key={stage.id}>
                <PopIn delay={(i % 3) * 0.08} className="h-full">
                  <div className="flex h-full flex-col gap-2 rounded-2xl border border-border bg-card px-6 py-5">
                    <div className="flex items-baseline gap-3">
                      <span className="font-display text-2xl leading-[1] font-bold italic text-primary">
                        {String(stage.number).padStart(2, '0')}
                      </span>
                      <h3 className="font-display text-[17px] leading-6 font-bold text-foreground">{stage.title}</h3>
                    </div>
                    {stage.duration && (
                      <span className="font-mono text-[10px] tracking-[0.6px] text-muted-foreground uppercase">
                        {stage.duration}
                      </span>
                    )}
                    <p className="text-sm leading-[22px] text-foreground-soft">{stage.summary}</p>
                  </div>
                </PopIn>
              </li>
            ))}
          </ol>
          <p className="text-[15px] leading-7 text-foreground-soft">
            {t('process.fullDetail')}{' '}
            <Link
              to="/start-aba-therapy"
              className="rounded-sm text-foreground underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('process.fullDetailLink')}
            </Link>
          </p>
        </div>
      </TracingBeam>
    </section>
  );
}

// ─── Testimonial ──────────────────────────────────────────────────────────────

function Testimonial() {
  const { t } = useTranslation('home');
  return (
    <section className="bg-background-alt px-4 md:px-6 lg:px-8 xl:px-24 py-16 md:py-24 lg:py-[120px]">
      <FadeIn whileInView y={16} duration={0.7}>
        <figure className="mx-auto flex max-w-[1040px] flex-col gap-8">
          <span aria-hidden="true" className="font-display h-16 text-[120px] leading-none text-primary">
            “
          </span>
          <blockquote>
            <p className="font-display text-2xl md:text-3xl lg:text-[40px] italic leading-[1.4] text-foreground">
              {t('testimonial.quote')}
            </p>
          </blockquote>
          <figcaption className="text-base text-foreground-soft">{t('testimonial.attribution')}</figcaption>
        </figure>
      </FadeIn>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

const FAQ_KEYS = ['bhField', 'aba', 'where', 'language', 'approach'] as const;

function Faq() {
  const { t } = useTranslation('home');
  const items = FAQ_KEYS.map((key) => ({ q: t(`faq.items.${key}.q`), a: t(`faq.items.${key}.a`) }));
  return (
    <section
      id="faq"
      aria-labelledby="faq-heading"
      className="scroll-mt-[120px] bg-background px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24"
    >
      <div className="mx-auto flex max-w-[1248px] flex-col gap-12 lg:flex-row lg:gap-24">
        <FadeIn whileInView y={12} duration={0.6} className="flex flex-col gap-6 lg:w-[420px] lg:shrink-0">
          <header className="flex flex-col gap-4">
            <p className="font-mono text-xs tracking-[1.44px] text-primary uppercase">{t('faq.eyebrow')}</p>
            <h2
              id="faq-heading"
              className="font-display text-3xl md:text-4xl lg:text-5xl lg:leading-[56px] font-bold text-foreground"
            >
              {t('faq.titleStart')} <span className="italic text-primary">{t('faq.titleAccent')}</span>
            </h2>
          </header>
          <p className="max-w-[380px] text-[15px] leading-6 text-foreground-soft">{t('faq.subtitle')}</p>
          <ScrollParallax distance={24} className="mt-4 hidden self-start lg:block">
            <AssetImage
              src={`${HOME_ART}/real-conversations.webp`}
              alt=""
              width={480}
              height={360}
              className="h-56 w-auto"
              loading="lazy"
            />
          </ScrollParallax>
        </FadeIn>
        <FadeIn whileInView delay={0.15} className="w-full lg:flex-1">
          <FaqAccordion
            items={items}
            triggerClassName="font-display text-lg font-bold"
            toggleClassName="font-display text-[24px] text-primary"
            contentClassName="text-[15px]"
          />
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Final CTA ────────────────────────────────────────────────────────────────

function FinalCta() {
  const { t } = useTranslation('home');
  return (
    <section className="bg-background-alt px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24">
      <TiltReveal maxTilt={12} className="mx-auto max-w-[1248px]">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-surface-pale px-6 py-12 md:px-10 md:py-16 lg:px-16 lg:py-[88px]">
          {/* Atmospheric gradient blobs behind the content (z-0 vs z-10). */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                'radial-gradient(ellipse 70% 80% at 85% 15%, color-mix(in oklch, var(--primary) 35%, transparent) 0%, color-mix(in oklch, var(--primary) 14%, transparent) 35%, transparent 75%)',
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                'radial-gradient(ellipse 65% 75% at 10% 90%, color-mix(in oklch, var(--accent-soft) 70%, var(--primary)) 0%, color-mix(in oklch, var(--accent-soft) 38%, transparent) 35%, transparent 75%)',
            }}
          />
          <div className="relative z-10 flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
            <div className="flex flex-1 flex-col items-center gap-7 text-center lg:items-start lg:text-left">
              <div className="flex flex-col gap-5">
                <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] lg:leading-[64px] font-bold text-foreground">
                  {t('finalCta.titleStart')} <span className="italic text-primary">{t('finalCta.titleAccent')}</span>?
                </h2>
                <p className="max-w-[680px] text-[17px] leading-7 text-foreground-soft">{t('finalCta.body')}</p>
              </div>
              <div className="flex flex-col items-center gap-4 lg:items-start">
                <a
                  href={GET_STARTED_URL}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3.5 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {t('finalCta.cta')} <ArrowRight className="size-4" strokeWidth={2} />
                </a>
                <p className="font-mono text-xs tracking-[1.1px] text-muted-foreground">{t('finalCta.phone')}</p>
              </div>
            </div>
            <ScrollParallax distance={20} className="w-full max-w-[300px] self-center lg:w-[300px] lg:shrink-0">
              <AssetImage
                src={`${HOME_ART}/open-door.webp`}
                alt=""
                width={480}
                height={360}
                className="aspect-[4/3] w-full object-contain"
                loading="lazy"
              />
            </ScrollParallax>
          </div>
        </div>
      </TiltReveal>
    </section>
  );
}

// ─── Clinic ───────────────────────────────────────────────────────────────────

function Clinic() {
  const { clinics } = useLoaderData<typeof loader>();
  const { t, i18n } = useTranslation('home');
  // An office is not a place a child comes for sessions, so it never stands in here.
  const clinic = clinics.find((site) => site.kind === 'clinic' && site.is_open);
  if (!clinic) return null;
  const image = clinic.image;

  return (
    <section
      id="clinic"
      aria-labelledby="clinic-heading"
      className="scroll-mt-[120px] bg-background px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24"
    >
      <div className="mx-auto flex max-w-[1248px] flex-col gap-10 lg:flex-row lg:items-center lg:gap-16">
        {image && (
          <TiltReveal maxTilt={10} className="w-full lg:w-[520px] lg:shrink-0">
            <AssetImage src={image} alt="" className="w-full rounded-2xl border border-border bg-card object-cover" />
          </TiltReveal>
        )}
        <FadeIn whileInView y={12} duration={0.6} className="flex flex-col gap-5 lg:flex-1">
          <p className="font-mono text-xs tracking-[1.44px] text-primary uppercase">{t('clinic.eyebrow')}</p>
          <h2
            id="clinic-heading"
            className="font-display text-3xl md:text-4xl lg:text-[44px] lg:leading-[52px] font-bold text-foreground"
          >
            {t('clinic.titleStart')} <span className="italic text-primary">{t('clinic.titleAccent')}</span>
          </h2>
          <p className="max-w-[620px] text-[17px] leading-7 text-foreground-soft">{t('clinic.body')}</p>
          <p className="max-w-[620px] border-l-2 border-primary pl-5 text-[15px] leading-6 text-foreground-soft italic">
            {t('clinic.growth')}
          </p>
          <p className="font-mono text-[11px] tracking-[1.1px] text-muted-foreground uppercase">
            {i18n.language === Languages.ES ? clinic.region_es : clinic.region_en} · {clinic.city}, {clinic.state}
          </p>
          <Link
            to="/get-started#clinics"
            className="inline-flex items-center gap-2 self-start rounded-md border border-border px-6 py-3.5 text-[15px] font-medium text-foreground transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t('clinic.cta')} <ArrowRight className="size-4" strokeWidth={2} />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Technology & research ────────────────────────────────────────────────────

const TECH_PILLARS = [
  { icon: Code2, key: 'builtInHouse' },
  { icon: LineChart, key: 'dataDriven' },
  { icon: Clock, key: 'lessAdmin' },
] as const;

function TechResearch() {
  const { t } = useTranslation('home');
  return (
    <section
      id="technology"
      aria-labelledby="technology-heading"
      className="scroll-mt-[120px] bg-background-alt px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24"
    >
      <div className="mx-auto flex max-w-[1248px] flex-col gap-12 md:gap-16">
        <FadeIn whileInView y={12} duration={0.6}>
          <header className="flex flex-col gap-4">
            <p className="font-mono text-xs tracking-[1.44px] text-primary uppercase">{t('techResearch.eyebrow')}</p>
            <h2
              id="technology-heading"
              className="font-display text-3xl md:text-4xl lg:text-[56px] lg:leading-[64px] font-bold text-foreground"
            >
              {t('techResearch.titleStart')}{' '}
              <span className="italic text-primary">{t('techResearch.titleAccent')}</span>
            </h2>
            <p className="max-w-[680px] text-lg leading-7 text-foreground-soft">{t('techResearch.subtitle')}</p>
          </header>
        </FadeIn>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {TECH_PILLARS.map(({ icon: Icon, key }, i) => (
            <PopIn key={key} delay={i * 0.1} className="h-full">
              <article className="flex h-full flex-col gap-5 rounded-2xl border border-border bg-card px-7 py-8">
                <div className="flex size-14 items-center justify-center rounded-2xl bg-surface-pale">
                  <Icon className="size-6 text-foreground-soft" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl leading-7 font-bold text-foreground">
                  {t(`techResearch.items.${key}.title`)}
                </h3>
                <p className="text-sm leading-[22px] text-foreground-soft">{t(`techResearch.items.${key}.body`)}</p>
              </article>
            </PopIn>
          ))}
        </div>

        {/* Research + collaboration invitation */}
        <PopIn delay={0.1}>
          <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card px-7 py-8 md:px-10 md:py-10 lg:flex-row lg:items-center lg:gap-12">
            <div className="flex size-14 flex-shrink-0 items-center justify-center rounded-2xl bg-surface-pale">
              <FlaskConical className="size-6 text-foreground-soft" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col gap-3 lg:flex-1">
              <h3 className="font-display text-2xl leading-8 font-bold text-foreground">
                {t('techResearch.research.title')}
              </h3>
              <p className="max-w-[760px] text-[15px] leading-6 text-foreground-soft">
                {t('techResearch.research.body')}
              </p>
              <p className="max-w-[760px] text-[15px] leading-6 font-medium text-foreground">
                {t('techResearch.research.collaborate')}
              </p>
            </div>
            <Link
              to="/contact#research"
              className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-md bg-primary px-6 py-3.5 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 lg:self-auto"
            >
              {t('techResearch.research.cta')} <ArrowRight className="size-4" strokeWidth={2} />
            </Link>
          </div>
        </PopIn>
      </div>
    </section>
  );
}

// ─── BH Field Academy ──────────────────────────────────────────────────────────

function Academy() {
  const { t } = useTranslation('home');
  return (
    <section
      aria-labelledby="academy-heading"
      className="bg-background px-4 md:px-6 lg:px-8 xl:px-24 py-12 md:py-16 lg:py-24"
    >
      <TiltReveal maxTilt={10} className="mx-auto max-w-[1248px]">
        <div className="relative flex flex-col gap-8 overflow-hidden rounded-3xl border border-border bg-surface-pale px-6 py-12 md:px-12 md:py-14 lg:flex-row lg:items-center lg:gap-16">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background:
                'radial-gradient(ellipse 60% 80% at 90% 20%, color-mix(in oklch, var(--primary) 26%, transparent) 0%, transparent 70%)',
            }}
          />
          <div className="relative z-10 flex flex-col gap-5 lg:flex-1">
            {/* Academy's own lockup — it is a distinct brand, so it keeps its own mark */}
            <AssetImage
              src="/images/academy/logo.svg"
              alt={t('academy.eyebrow')}
              width={429}
              height={163}
              className="h-16 w-auto self-start md:h-20"
            />
            <h2
              id="academy-heading"
              className="font-display text-3xl md:text-4xl lg:text-[44px] lg:leading-[52px] font-bold text-foreground"
            >
              {t('academy.titleStart')} <span className="italic text-primary">{t('academy.titleAccent')}</span>
            </h2>
            <p className="max-w-[640px] text-[17px] leading-7 text-foreground-soft">{t('academy.body')}</p>
          </div>
          <div className="relative z-10 flex flex-col items-start gap-3 lg:flex-shrink-0">
            <a
              href={ACADEMY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3.5 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {t('academy.cta')} <ArrowRight className="size-4" strokeWidth={2} />
            </a>
            <p className="font-mono text-[10px] tracking-[0.9px] text-muted-foreground uppercase">
              {t('academy.note')}
            </p>
          </div>
        </div>
      </TiltReveal>
    </section>
  );
}
